// Zero-cost lesson illustration system.
//
// We would love to serve real generated images (Gemini Nano Banana would
// be ideal) but running one image gen per lesson section is expensive
// and blows the Universal LLM key budget in the middle of large curriculum
// batches. So this file provides a beautiful *deterministic* fallback:
// each illustration is a colorful gradient + a curated emoji collage
// derived from the section's `image_prompt`, `heading`, and `key_terms`.
//
// The mapping is topic-aware: a section prompt mentioning "cell" or
// "biology" produces microscope + DNA + petri emojis on a green bg;
// a prompt about "Egypt" produces pyramid + camel + sun on a warm sand
// palette. If none of the topic keywords match, we fall back to
// section-type defaults (intro / activity / check / etc).
//
// Consumers:
//   • <IllustrationSlot /> in LessonReader.jsx renders the collage.
//   • Backend/`illustration_url` (if ever added later) always wins.

// ── Keyword → { emojis, gradient } mapping ─────────────────────────
// Ordered by specificity (first match wins).
const TOPIC_MAP = [
  // Geography
  { keys: ["continent","world map","atlas","longitude","latitude","meridian"], emojis: ["🗺️","🧭","🌍","📍"], grad: "from-emerald-100 via-sky-100 to-blue-100" },
  { keys: ["mountain","alps","himalaya","everest"],                             emojis: ["🏔️","🌲","🥾","☁️"], grad: "from-slate-100 via-blue-100 to-emerald-100" },
  { keys: ["river","nile","amazon","water cycle","ocean","sea"],                emojis: ["🌊","🐟","🚣","💧"], grad: "from-sky-100 via-cyan-100 to-blue-200" },
  { keys: ["desert","sahara"],                                                  emojis: ["🐫","🌵","☀️","🏜️"], grad: "from-amber-100 via-orange-100 to-yellow-100" },
  { keys: ["forest","rainforest","jungle","amazon rainforest","tree"],          emojis: ["🌳","🐒","🦋","🍃"], grad: "from-lime-100 via-emerald-100 to-green-100" },
  { keys: ["climate","weather","biome"],                                        emojis: ["☀️","☁️","🌧️","🌡️"], grad: "from-sky-100 via-yellow-50 to-blue-100" },

  // Countries / landmarks
  { keys: ["egypt","pyramid"],                                                  emojis: ["🐫","🏜️","🐍","🏛️"], grad: "from-yellow-100 via-amber-100 to-orange-100" },
  { keys: ["greece","athens","parthenon"],                                      emojis: ["🏛️","🫒","⚱️","🌊"], grad: "from-sky-100 via-blue-100 to-white" },
  { keys: ["rome","roman","colosseum"],                                         emojis: ["🏛️","⚔️","🍇","🗝️"], grad: "from-rose-100 via-amber-100 to-orange-100" },
  { keys: ["japan","tokyo","fuji"],                                             emojis: ["🗾","🌸","⛩️","🏯"], grad: "from-rose-100 via-pink-100 to-red-100" },
  { keys: ["india","taj mahal"],                                                emojis: ["🕌","🐘","🌶️","🪔"], grad: "from-orange-100 via-yellow-100 to-red-100" },
  { keys: ["china","great wall"],                                               emojis: ["🐉","🏯","🎋","🥟"], grad: "from-red-100 via-yellow-100 to-orange-100" },

  // Science - Biology
  { keys: ["cell","biology","organism","dna","microscope"],                     emojis: ["🔬","🧬","🦠","🧫"], grad: "from-emerald-100 via-teal-100 to-green-100" },
  { keys: ["plant","flower","photosynthesis","leaf"],                           emojis: ["🌱","🌻","🌿","🍃"], grad: "from-lime-100 via-green-100 to-emerald-100" },
  { keys: ["animal","habitat","ecosystem","wildlife"],                          emojis: ["🦁","🐘","🦒","🌍"], grad: "from-amber-100 via-yellow-100 to-lime-100" },
  { keys: ["human body","skeleton","heart","brain","muscle"],                   emojis: ["🫀","🧠","🦴","💪"], grad: "from-rose-100 via-pink-100 to-red-100" },

  // Science - Physics
  { keys: ["force","gravity","motion","energy","magnet"],                       emojis: ["🧲","⚡","🚀","🌌"], grad: "from-indigo-100 via-violet-100 to-purple-100" },
  { keys: ["light","rainbow","color","prism"],                                  emojis: ["🌈","💡","☀️","🔦"], grad: "from-yellow-100 via-pink-100 to-purple-100" },
  { keys: ["sound","wave","hearing","music"],                                   emojis: ["🎵","🔊","🎧","🎼"], grad: "from-violet-100 via-fuchsia-100 to-pink-100" },

  // Space
  { keys: ["planet","solar system","earth","moon","sun","space","star","galaxy"], emojis: ["🌍","🌕","☀️","🚀"], grad: "from-slate-100 via-indigo-100 to-violet-100" },

  // Maths
  { keys: ["place value","number","numeral","digit","addition","subtraction","multipl","division","fraction","decimal","percent","scaling"], emojis: ["🔢","➕","✖️","🧮"], grad: "from-yellow-100 via-amber-100 to-orange-100" },
  { keys: ["shape","geometry","angle","triangle","circle"],                     emojis: ["📐","📏","🔷","🔺"], grad: "from-sky-100 via-cyan-100 to-teal-100" },
  { keys: ["algebra","equation","variable"],                                    emojis: ["🧮","🔢","➗","📊"], grad: "from-indigo-100 via-purple-100 to-violet-100" },
  { keys: ["measure","time","clock","hour","calendar"],                         emojis: ["🕒","⏳","📅","⌛"], grad: "from-yellow-100 via-orange-100 to-rose-100" },
  { keys: ["money","currency","dollar","pound","euro"],                         emojis: ["💰","💵","🪙","💴"], grad: "from-green-100 via-lime-100 to-yellow-100" },
  { keys: ["data","graph","chart","statistic"],                                 emojis: ["📊","📈","📉","🧮"], grad: "from-sky-100 via-blue-100 to-indigo-100" },

  // English / Literacy
  { keys: ["read","reading","story","book","fiction","poetry"],                 emojis: ["📖","🕮","📚","🔖"], grad: "from-amber-100 via-yellow-100 to-orange-100" },
  { keys: ["writ","spelling","alphabet","letter","vocab"],                      emojis: ["✍️","📝","🔤","📓"], grad: "from-sky-100 via-cyan-100 to-blue-100" },
  { keys: ["listen","speak","conversation","phonic"],                           emojis: ["🎧","🗣️","👂","💬"], grad: "from-violet-100 via-fuchsia-100 to-pink-100" },

  // History
  { keys: ["history","ancient","civilization","medieval","viking","empire"],    emojis: ["🏛️","📜","🗝️","⚔️"], grad: "from-amber-100 via-orange-100 to-rose-100" },
  { keys: ["war","battle","soldier"],                                           emojis: ["⚔️","🏰","🛡️","🎖️"], grad: "from-slate-100 via-rose-100 to-red-100" },

  // Art / Music
  { keys: ["art","paint","draw","sculpt","design"],                             emojis: ["🎨","🖌️","🖼️","🎭"], grad: "from-fuchsia-100 via-pink-100 to-rose-100" },
  { keys: ["music","instrument","song","sing","note"],                          emojis: ["🎵","🎹","🎸","🥁"], grad: "from-violet-100 via-fuchsia-100 to-purple-100" },

  // Languages
  { keys: ["language","french","spanish","mandarin","greeting"],                emojis: ["🗣️","🌐","💬","📚"], grad: "from-sky-100 via-blue-100 to-indigo-100" },

  // ICT / Computing
  { keys: ["algorithm","program","code","computer","debug"],                    emojis: ["💻","⌨️","🤖","🧩"], grad: "from-slate-100 via-sky-100 to-cyan-100" },

  // PE / Sport
  { keys: ["run","jump","throw","catch","game","team","fitness","sport"],       emojis: ["🏃","⚽","🏅","💪"], grad: "from-lime-100 via-yellow-100 to-orange-100" },

  // RE / faith
  { keys: ["religion","faith","festival","celebrat","worship","tradition"],     emojis: ["🕊️","🕯️","🎊","🙏"], grad: "from-yellow-100 via-orange-100 to-pink-100" },

  // PSHE / wellbeing
  { keys: ["friend","feeling","emotion","kind","mental health","wellbeing","respect"], emojis: ["🤝","💛","🌈","🫶"], grad: "from-pink-100 via-rose-100 to-yellow-100" },
];

// ── Section-type defaults (used when no topic keyword matches) ─────
const TYPE_MAP = {
  intro:      { emojis: ["✨","🎯","🌟","💫"], grad: "from-yellow-100 via-amber-100 to-orange-100" },
  discover:   { emojis: ["🔍","💡","🌟","✨"], grad: "from-sky-100 via-cyan-100 to-blue-100" },
  activity:   { emojis: ["🎨","✂️","📝","🎯"], grad: "from-lime-100 via-emerald-100 to-green-100" },
  practice:   { emojis: ["✏️","📋","💪","🎯"], grad: "from-amber-100 via-yellow-100 to-lime-100" },
  check:      { emojis: ["✅","🧠","💭","🎯"], grad: "from-emerald-100 via-teal-100 to-cyan-100" },
  reflect:    { emojis: ["💭","🌱","💡","🤔"], grad: "from-violet-100 via-fuchsia-100 to-pink-100" },
  challenge:  { emojis: ["🏆","🔥","⚡","💪"], grad: "from-orange-100 via-rose-100 to-red-100" },
  vocabulary: { emojis: ["📚","🔤","✍️","💬"], grad: "from-sky-100 via-blue-100 to-indigo-100" },
};

const FALLBACK = { emojis: ["🌍","📚","✨","💡"], grad: "from-sky-100 via-emerald-100 to-yellow-100" };

/**
 * Pick a collage for a lesson section.
 *
 * @param {{ type?: string, prompt?: string, heading?: string, keyTerms?: string[] }} input
 * @returns {{ emojis: string[], grad: string }}
 */
export function pickIllustration({ type, prompt, heading, keyTerms } = {}) {
  const hay = [
    prompt || "",
    heading || "",
    ...(keyTerms || []).map((k) => (typeof k === "string" ? k : k?.term || "")),
  ].join(" ").toLowerCase();

  for (const rule of TOPIC_MAP) {
    if (rule.keys.some((k) => hay.includes(k))) return rule;
  }
  if (type && TYPE_MAP[type]) return TYPE_MAP[type];
  return FALLBACK;
}
